import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, startOfWeek, format } from 'date-fns';
import './TimesheetEditPage.css';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { useTimesheet } from './context/TimesheetContext';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const statusColors = {
  draft: '#bdbdbd',
  pending: '#f1c40f',
  approved: '#388e3c',
  rejected: '#d32f2f',
};

// Helper function to parse date string (YYYY-MM-DD) as local date to avoid timezone issues
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  // Create date in local timezone (month is 0-indexed)
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
};


const TimesheetEditPage = ({ mode }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { startDate: contextStartDate, endDate: contextEndDate } = useTimesheet();
  const [form, setForm] = useState({
    fromDate: null,
    toDate: null,
    approver: '',
    project: '',
    standardTime: '',
    overtime: '',
    status: 'draft',
  });
  const [isEditingDisabled, setIsEditingDisabled] = useState(false);
  const [weekStart] = useState(new Date());
  const [weekData, setWeekData] = useState({
    st: Array(7).fill('0'),
    ot: Array(7).fill('0'),
    tsdIds: Array(7).fill(null), // Add this to store existing tsdId values
    timeStyles: Array(7).fill('R'), // Add timeStyle for each day, default to 'R' (Regular)
  });
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [existingTimesheets, setExistingTimesheets] = useState([]);

  // Project options come from the logged-in user (user.projects)
  const projectOptions = (user && Array.isArray(user.projects))
    ? user.projects.map(p => ({ projectId: p.projectId, projectCode: p.projectCode, description: p.description }))
    : [];

  // Get timesheet data from navigation state if available
  const navState = location.state && location.state.timesheet;
  const id = paramId;

  // Populate approver name from user in Redux when creating a new timesheet
  useEffect(() => {
    if (!id && user && user.approver) {
      const approverName = user.approver.firstName || user.approver.lastName
        ? `${user.approver.firstName || ''} ${user.approver.lastName || ''}`.trim()
        : '';
      if (approverName) {
        setForm(prev => ({ ...prev, approver: approverName }));
      }
    }
  }, [id, user]);

  // Fetch existing timesheets when creating a new timesheet to check for duplicates
  useEffect(() => {
    let ignore = false;
    if (!id && user) {
      async function fetchExistingTimesheets() {
        try {
          const response = await axios.get(
            `${API_BASE_URL}timesheet/all?userId=${user.userId}&projectId=&from=&to=&orgId=${user.department.costCenter.organization.orgId}`
          );
          if (!ignore) {
            const apiData = response.data || [];
            console.log('Fetched existing timesheets for duplicate check:', apiData.length, 'timesheets');
            console.log('Existing timesheet dates:', apiData.map(ts => ts.dateFrom));
            setExistingTimesheets(apiData);
          }
        } catch (err) {
          console.error('Error fetching existing timesheets:', err);
          // Don't set error state here, just log it
        }
      }
      fetchExistingTimesheets();
    } else {
      setExistingTimesheets([]);
    }
    return () => { ignore = true; };
  }, [id, user]);

  useEffect(() => {
    let ignore = false;
    if (!id) {
      setLoading(false);
      setIsEditingDisabled(false);
      return;
    }
    async function fetchTimesheet() {
      setLoading(true);
      setError(null);
      try {
        if (navState && navState.id === id) {
          const ts = navState;
          setForm({
            fromDate: ts.fromDate ? parseLocalDate(ts.fromDate) : null,
            toDate: ts.toDate ? parseLocalDate(ts.toDate) : null,
            approver: ts.approver || '',
            project: ts.project || '',
            standardTime: ts.standardTime || '',
            overtime: ts.overtime || '',
            status: ts.status || 'draft',
          });
          setIsEditingDisabled(!(ts.status === 'draft' || ts.status === 'rejected'));
          setLoading(false);
          return;
        }
        if (!user || !id) throw new Error('Missing auth or id');
        const response = await axios.get(
          `${API_BASE_URL}timesheet/all?userId=${user.userId}&projectId=&from=&to=&orgId=${user.department.costCenter.organization.orgId}`
        );
        const apiData = response.data || [];
        const found = apiData.find(ts => String(ts.tsId) === String(id));
        if (!found) throw new Error('Timesheet not found');

        // Map status number to string
        const statusMap = { 1: 'draft', 2: 'pending', 3: 'approved', 4: 'rejected' };
        // Map weekData from timeSheetDetailList
        const stArr = Array(7).fill('0');
        const otArr = Array(7).fill('0');
        const tsdIdArr = Array(7).fill(null); // Add this array for tsdIds
        const timeStyleArr = Array(7).fill('R'); // Default to 'R' if not provided

        if (Array.isArray(found.timeSheetDetailList)) {
          found.timeSheetDetailList.forEach(detail => {
            const idx = (detail.dayOfWeek - 1); // dayOfWeek: 1=Mon, 7=Sun
            const timeStyle = detail.timeStyle || 'R';
            timeStyleArr[idx] = timeStyle; // Store the timeStyle, default to 'R'

            // For non-Regular timeStyles, set both ST and OT to 0
            if (timeStyle !== 'R') {
              stArr[idx] = '0';
              otArr[idx] = '0';
            } else {
              // For Regular, use the actual values from the API
              stArr[idx] = detail.standardTimeAmt != null ? String(detail.standardTimeAmt) : '0';
              otArr[idx] = detail.overTimeAmt != null ? String(detail.overTimeAmt) : '0';
            }

            tsdIdArr[idx] = detail.tsdId; // Store the existing tsdId
          });
        }

        setForm({
          fromDate: found.dateFrom ? parseLocalDate(found.dateFrom) : null,
          toDate: found.dateTo ? parseLocalDate(found.dateTo) : null,
          approver: found.approver
            ? `${found.approver.firstName || ''} ${found.approver.lastName || ''}`.trim()
            : '',
          project: found.project ? found.project.projectCode : '',
          standardTime: found.stSubtotal || '',
          overtime: found.otSubtotal || '',
          status: statusMap[found.status] || 'draft',
        });
        setWeekData({ st: stArr, ot: otArr, tsdIds: tsdIdArr, timeStyles: timeStyleArr }); // Include tsdIds and timeStyles
        setComment(found.description || '');
        setIsEditingDisabled(!(found.status === 1 || found.status === 4 || statusMap[found.status] === 'draft' || statusMap[found.status] === 'rejected'));
      } catch (err) {
        setError(err.message || 'Error loading timesheet');
      } finally {
        setLoading(false);
      }
    }
    fetchTimesheet();
    return () => { ignore = true; };
  }, [id, user, navState]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to check if a week already exists
  const isWeekDuplicate = (mondayDate) => {
    if (!mondayDate || id) return false; // Only check for new timesheets (when id is null/undefined)
    if (existingTimesheets.length === 0) return false; // No existing timesheets, so no duplicate
    const selectedWeekStart = format(mondayDate, 'yyyy-MM-dd');
    const isDuplicate = existingTimesheets.some(ts => ts.dateFrom === selectedWeekStart);
    console.log('Checking duplicate for week:', selectedWeekStart, 'Is duplicate:', isDuplicate);
    return isDuplicate;
  };


  // Helper to build API request body
  const buildTimesheetPayload = (statusOverride) => {
    // Find projectId from projectOptions or form.project
    let projectId = null;
    if (user && user.projects && user.projects.length > 0) {
      // Try to match project code
      const found = user.projects.find(p => p.projectCode === form.project);
      if (found) projectId = found.projectId;
    }
    // Fallback: if form.project is a number
    if (!projectId && !isNaN(Number(form.project))) projectId = Number(form.project);

    // Validate projectId - must be selected
    if (!projectId) {
      throw new Error('Please select a project before saving');
    }

    // Approver userId from user in redux
    let approverId = null;
    if (user && user.approver && user.approver.userId) {
      approverId = user.approver.userId;
    }

    // Validate approverId - must exist
    if (!approverId) {
      throw new Error('Approver information is missing. Please contact your administrator.');
    }

    // Department, org, etc.
    const deptId = user && user.department && user.department.deptId ? user.department.deptId : 2;
    const orgId = 1;
    const createUid = user && user.userId ? user.userId : 15;
    const updateUid = user && user.userId ? user.userId : 12;

    // Build timeSheetDetailList - include ALL 7 days
    const timeSheetDetailList = [];
    for (let i = 0; i < 7; i++) {
      const standardTime = weekData.st[i] ? Number(weekData.st[i]) : 0;
      const overTime = weekData.ot[i] ? Number(weekData.ot[i]) : 0;
      const timeStyle = weekData.timeStyles && weekData.timeStyles[i] ? weekData.timeStyles[i] : 'R';

      timeSheetDetailList.push({
        tsdId: weekData.tsdIds ? weekData.tsdIds[i] : null,
        tsId: id ? Number(id) : null,
        dayOfWeek: i + 1,
        standardTimeAmt: standardTime,
        overTimeAmt: overTime,
        timeStyle: timeStyle,
        createUid,
        updateUid
      });
    }

    // Validate dates - they are required
    if (!form.fromDate || !form.toDate) {
      throw new Error('Please select both From Date and To Date before saving');
    }

    return {
      tsId: id ? Number(id) : null,
      timeSheetDetailList,
      department: { deptId },
      user: { userId: createUid },
      approver: { userId: approverId },
      project: { projectId },
      organization: { orgId },
      stSubtotal: stTotal,
      otSubtotal: otTotal,
      total: weekTotal,
      dateFrom: format(form.fromDate, 'yyyy-MM-dd'),
      dateTo: format(form.toDate, 'yyyy-MM-dd'),
      status: statusOverride != null ? statusOverride : (form.status === 'draft' ? 1 : 2),
      description: comment,
      createUid,
      updateUid
    };
  };

  // Save (draft or update) - using POST only
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields before building payload
      if (!form.fromDate || !form.toDate) {
        setError('Please select Date before saving');
        return;
      }
      if (!form.project) {
        setError('Please select project before saving');
        return;
      }
      // Check for duplicate week when creating new timesheet
      if (!id && isWeekDuplicate(form.fromDate)) {
        setError('A timesheet for this week already exists. Please select a different week.');
        setLoading(false);
        return;
      }

      const payload = buildTimesheetPayload(1); // status 1 = draft
      //print to console for error checking (may be removed later)////////////////////////////////////
      console.log('Saving timesheet with payload:', JSON.stringify(payload, null, 2));
      console.log('API URL:', `${API_BASE_URL}timesheet`);

      // Always use POST for both new and existing timesheets
      await axios.post(
        `${API_BASE_URL}timesheet`,
        payload
      );

      // Navigate back to list and instruct it to refresh using context dates (not payload dates)
      const from = contextStartDate ? format(contextStartDate, 'yyyy-MM-dd') : '';
      const to = contextEndDate ? format(contextEndDate, 'yyyy-MM-dd') : '';
      navigate('/timesheets', { state: { refresh: true, filters: { from, to } } });
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.message || 'Error saving timesheet');
    } finally {
      setLoading(false);
    }
  };

  // Submit (set status to pending) - using POST only
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields before building payload
      if (!form.fromDate || !form.toDate) {
        setError('Please select Date before submitting');
        return;
      }
      if (!form.project) {
        setError('Please select project before submitting');
        return;
      }
      // Check for duplicate week when creating new timesheet
      if (!id && isWeekDuplicate(form.fromDate)) {
        setError('A timesheet for this week already exists. Please select a different week.');
        setLoading(false);
        return;
      }

      const payload = buildTimesheetPayload(2); // status 2 = pending

      //print to console for error checking (may be removed later)////////////////////////////////////
      console.log('Submitting timesheet with payload:', JSON.stringify(payload, null, 2));
      console.log('API URL:', `${API_BASE_URL}timesheet`);

      // Always use POST for both new and existing timesheets
      await axios.post(
        `${API_BASE_URL}timesheet`,
        payload
      );

      // Navigate back to list and instruct it to refresh using context dates (not payload dates)
      const from = contextStartDate ? format(contextStartDate, 'yyyy-MM-dd') : '';
      const to = contextEndDate ? format(contextEndDate, 'yyyy-MM-dd') : '';
      navigate('/timesheets', { state: { refresh: true, filters: { from, to } } });
    } catch (err) {
      console.error('Submit error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.message || 'Error submitting timesheet');
    } finally {
      setLoading(false);
    }
  };


  // Helper to get week dates (Monday through Sunday).
  // If a From date is selected it must be Monday and we use that as the base; otherwise fall back to the current weekStart.
  const baseMonday = form.fromDate ? startOfWeek(form.fromDate, { weekStartsOn: 1 }) : startOfWeek(weekStart, { weekStartsOn: 1 });
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(baseMonday, i));
  // Day names for translation
  const dayNames = [
    t('timesheet.mon', 'Mon'),
    t('timesheet.tue', 'Tue'),
    t('timesheet.wed', 'Wed'),
    t('timesheet.thu', 'Thu'),
    t('timesheet.fri', 'Fri'),
    t('timesheet.sat', 'Sat'),
    t('timesheet.sun', 'Sun'),
  ];
  const handleWeekDataChange = (type, idx, value) => {
    setWeekData((prev) => {
      const arr = [...prev[type]];
      arr[idx] = value;
      return { ...prev, [type]: arr };
    });
  };

  // Handle timeStyle dropdown change
  const handleTimeStyleChange = (idx, value) => {
    setWeekData((prev) => {
      const timeStyles = [...prev.timeStyles];
      timeStyles[idx] = value;

      const st = [...prev.st];
      const ot = [...prev.ot];

      // For non-Regular timeStyles, set both ST and OT to 0
      if (value !== 'R') {
        // O-Off, H-Holiday, V-Vacation, P-Personal, S-Sick => 0 hrs for both ST and OT
        st[idx] = '0';
        ot[idx] = '0';
      }
      // R-Regular => keep existing values (user can edit)

      return { ...prev, timeStyles, st, ot };
    });
  };
  const stTotal = weekData.st.reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const otTotal = weekData.ot.reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const dayTotals = weekData.st.map((st, i) => (parseFloat(st) || 0) + (parseFloat(weekData.ot[i]) || 0));
  const weekTotal = stTotal + otTotal;

  const formatTotal = (num) => num ? num.toFixed(2) : '0.00';

  if (loading) {
    return <div className="ts-edit-container">{t('loading', 'Loading...')}</div>;
  }

  return (
    <div className="ts-edit-container">
      {/* Error display */}
      {error && (
        <div className="ts-edit-error">
          {error}
        </div>
      )}
      <h2 className="ts-edit-title">{t('timesheet.info')}</h2>
      <div className="ts-edit-info-row">
        <div
          className="ts-edit-status"
          style={{
            background: statusColors[form.status] || '#bdbdbd',
          }}
        >
          {t('timesheet.status')}: {t(`timesheet.${form.status}`, form.status.charAt(0).toUpperCase() + form.status.slice(1))}
        </div>
        {id && (
          <span className="ts-edit-meta">
            {t('timesheet.id')}: {id}
          </span>
        )}
        <span className="ts-edit-meta">
          {t('timesheet.org')}: {user?.department?.costCenter?.organization?.orgName || '-'}
        </span>
        <span className="ts-edit-meta">
          {t('timesheet.costCenter')}: {user?.department?.costCenter?.ccName || '-'}
        </span>
        <span className="ts-edit-meta">
          {t('timesheet.dept')}: {user?.department?.deptName || '-'}
        </span>
        <span className="ts-edit-meta">
          {t('timesheet.approver')}: {form.approver || '-'}
        </span>
        <span className="ts-edit-meta">
          {t('timesheet.user')}: {user?.firstName || ''} {user?.lastName || ''}
        </span>
      </div>

      {/* Project / From / To row */}
      <div className="ts-edit-project-date-row">
        {/* Left: Project dropdown */}
        <div className="ts-edit-project">
          <label className="ts-edit-label-inline" htmlFor="project-select">
            {t('timesheet.project') || 'Project'}
          </label>
          <select
            id="project-select"
            name="project"
            value={form.project}
            onChange={handleChange}
            disabled={isEditingDisabled || id}
            className="ts-edit-project-select"
          >
            <option value="">{t('timesheet.selectProject')}</option>
            {projectOptions.length > 0 ? (
              projectOptions.map((opt) => (
                <option key={opt.projectId} value={opt.projectCode}>{opt.projectCode}</option>
              ))
            ) : (
              <option value="">{t('timesheet.noProjects', 'No projects available')}</option>
            )}
          </select>
        </div>

        {/* Right: From + To date pickers */}
        <div className="ts-edit-dates">
          <div className="ts-edit-row-item">
            <label className="ts-edit-label-inline">{t('timesheet.fromDate')}</label>
            <DatePicker
              selected={form.fromDate}
              onChange={(date) => {
                // Only accept Mondays (JS: 1 === Monday)
                if (!date) {
                  setForm(prev => ({ ...prev, fromDate: null, toDate: null }));
                  setWeekData({ st: Array(7).fill('0'), ot: Array(7).fill('0'), tsdIds: Array(7).fill(null), timeStyles: Array(7).fill('R') });
                  setError(null); // Clear error when clearing date
                  return;
                }
                if (date.getDay() !== 1) {
                  // Not Monday — clear the value per requirement
                  setForm(prev => ({ ...prev, fromDate: null, toDate: null }));
                  setWeekData({ st: Array(7).fill('0'), ot: Array(7).fill('0'), tsdIds: Array(7).fill(null), timeStyles: Array(7).fill('R') });
                  return;
                }
                // Check for duplicate week when creating new timesheet
                if (!id && isWeekDuplicate(date)) {
                  setError('A timesheet for this week already exists. Please select a different week.');
                  setForm(prev => ({ ...prev, fromDate: null, toDate: null }));
                  setWeekData({ st: Array(7).fill('0'), ot: Array(7).fill('0'), tsdIds: Array(7).fill(null), timeStyles: Array(7).fill('R') });
                  return;
                }
                setError(null); // Clear error if date is valid
                const to = addDays(date, 6);
                setForm(prev => ({ ...prev, fromDate: date, toDate: to }));
                // If creating new timesheet, reset week data to 0 so user can enter hours
                if (!id) {
                  setWeekData({ st: Array(7).fill('0'), ot: Array(7).fill('0'), tsdIds: Array(7).fill(null), timeStyles: Array(7).fill('R') });
                }
              }}
              filterDate={date => {
                // Only show Mondays
                if (date.getDay() !== 1) return false;
                // When creating new timesheet, exclude dates that would create duplicates
                if (!id) {
                  return !isWeekDuplicate(date);
                }
                return true;
              }}
              dateFormat="yyyy-MM-dd"
              className="ts-edit-date-picker"
              placeholderText="Select From Date (Monday)"
              disabled={isEditingDisabled || id}
            />
          </div>

          <div className="ts-edit-row-item">
            <label className="ts-edit-label-inline">{t('timesheet.toDate')}</label>
            <DatePicker
              selected={form.toDate}
              onChange={() => { /* read-only: computed from From date */ }}
              dateFormat="yyyy-MM-dd"
              className="ts-edit-date-picker"
              placeholderText="Auto-calculated to (Sunday)"
              disabled
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="ts-edit-table-wrapper">
        <table className="ts-edit-table">
          <thead>
            <tr>
              <th className="ts-edit-th" style={{ width: 80 }}>{t('timesheet.day')}</th>
              {weekDates.map((date, i) => (
                <th className="ts-edit-th" key={i}>
                  <div className="ts-edit-day-header">
                    <select
                      className="ts-edit-day-style"
                      value={weekData.timeStyles && weekData.timeStyles[i] ? weekData.timeStyles[i] : 'R'}
                      onChange={(e) => handleTimeStyleChange(i, e.target.value)}
                      disabled={isEditingDisabled}
                    >
                      <option value="R">{t("timesheet.R")}</option>
                      <option value="O">{t("timesheet.O")}</option>
                      <option value="H">{t("timesheet.H")}</option>
                      <option value="V">{t("timesheet.V")}</option>
                      <option value="P">{t("timesheet.P")}</option>
                      <option value="S">{t("timesheet.S")}</option>
                    </select>
                    <span className="ts-edit-day-name">{dayNames[i]}</span>
                  </div>
                  <div className="ts-edit-day-date">{format(date, 'yyyy-MM-dd')}</div>
                </th>
              ))}
              <th className="ts-edit-th">{t('timesheet.total')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="ts-edit-row-label">{t('timesheet.st')}</td>
              {weekData.st.map((v, i) => {
                const isRegular = weekData.timeStyles && weekData.timeStyles[i] === 'R';
                const isDisabled = isEditingDisabled || !isRegular;
                return (
                  <td className="ts-edit-td" key={i}>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={v}
                      disabled={isDisabled}
                      onChange={e => handleWeekDataChange('st', i, e.target.value)}
                      className="ts-edit-input"
                    />
                  </td>
                );
              })}
              <td className="ts-edit-td ts-edit-total-cell">{formatTotal(stTotal)}</td>
            </tr>
            <tr>
              <td className="ts-edit-row-label">{t('timesheet.ot')}</td>
              {weekData.ot.map((v, i) => {
                const isRegular = weekData.timeStyles && weekData.timeStyles[i] === 'R';
                const isDisabled = isEditingDisabled || !isRegular;
                return (
                  <td className="ts-edit-td" key={i}>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={v}
                      disabled={isDisabled}
                      onChange={e => handleWeekDataChange('ot', i, e.target.value)}
                      className="ts-edit-input"
                    />
                  </td>
                );
              })}
              <td className="ts-edit-td ts-edit-total-cell">{formatTotal(otTotal)}</td>
            </tr>
            <tr>
              <td className="ts-edit-row-label">{t('timesheet.total')}</td>
              {dayTotals.map((v, i) => (
                <td className="ts-edit-td ts-edit-total-cell" key={i}>{formatTotal(v)}</td>
              ))}
              <td className="ts-edit-td ts-edit-grand-total">{formatTotal(weekTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="ts-edit-comment-block">
        <label className="ts-edit-label">{t('timesheet.comment')}:</label>
        <textarea
          className="ts-edit-comment-box"
          value={comment}
          onChange={e => setComment(e.target.value)}
          disabled={isEditingDisabled}
          placeholder={t('timesheet.addComment')}
          maxLength={512}
          rows={4}
        />
        <div className="ts-edit-comment-count">
          {comment.length}/512
        </div>
      </div>
      <div className="ts-edit-btn-row">
        {!isEditingDisabled && (
          <>
            <button type="button" onClick={handleSave} className="ts-edit-btn save">
              {t('timesheet.save')}
            </button>
            <button type="button" onClick={handleSubmit} className="ts-edit-btn submit">
              {t('timesheet.submit')}
            </button>
          </>
        )}
        <button type="button" onClick={() => navigate('/timesheets')} className="ts-edit-btn cancel">
          {t('timesheet.cancel')}
        </button>
      </div>
    </div>
  );
};

export default TimesheetEditPage;